<template>
  <nav
    role="tablist"
    :aria-label="ariaLabel"
    class="-mx-1 flex flex-wrap justify-end gap-2 px-1 py-1"
  >
    <component
      :is="tab.to ? 'router-link' : 'button'"
      v-for="(tab, index) in tabs"
      :key="tab.value"
      ref="tabRefs"
      :to="tab.to || undefined"
      :type="tab.to ? undefined : 'button'"
      role="tab"
      :data-tab-value="tab.value"
      :aria-selected="isActive(tab) ? 'true' : 'false'"
      :tabindex="isActive(tab) ? 0 : -1"
      :class="[
        'flex-none inline-flex max-w-full items-center gap-1.5 whitespace-normal rounded-full px-4 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-palette-emerald/30 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-theme-bg',
        isActive(tab)
          ? 'bg-theme-brand text-theme-text-inverse hover:bg-theme-brand-hover'
          : 'text-theme-text-secondary hover:bg-theme-bg-subtle hover:text-theme-text dark:hover:bg-theme-surface-raised dark:hover:text-theme-text-muted',
      ]"
      @click="onClickTab(tab, $event)"
      @keydown.right.prevent="focusTab(index + 1)"
      @keydown.left.prevent="focusTab(index - 1)"
      @keydown.home.prevent="focusTab(0)"
      @keydown.end.prevent="focusTab(tabs.length - 1)"
    >
      <Icon v-if="tab.icon" :path="tab.icon" :size="14" />
      <span class="min-w-0 [overflow-wrap:anywhere]">{{ tab.label }}</span>
    </component>
  </nav>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import { computed, ref } from 'vue';

  const props = defineProps({
    modelValue: { type: [String, Number, null], default: null },
    tabs: { type: Array, required: true },
    ariaLabel: { type: String, default: '' },
  });
  const emit = defineEmits(['update:modelValue', 'select']);

  const tabRefs = ref([]);

  function isActive(tab) {
    return props.modelValue !== null && props.modelValue !== undefined && tab.value === props.modelValue;
  }

  function onClickTab(tab, event) {
    if (tab.to) {
      emit('select', tab);
      return;
    }
    event?.preventDefault?.();
    if (tab.value !== props.modelValue) emit('update:modelValue', tab.value);
    emit('select', tab);
  }

  function focusTab(index) {
    const total = props.tabs.length;
    if (!total) return;
    const wrapped = ((index % total) + total) % total;
    const refs = tabRefs.value;
    const el = refs[wrapped];
    const focusable = el?.$el || el;
    if (focusable?.focus) focusable.focus();
  }

  // expose so parent can access selected tab metadata via slot scope if needed
  defineExpose({
    focusTab,
    selectedTab: computed(() => props.tabs.find(isActive) || null),
  });
</script>
